"use client";

import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Flame, ShieldCheck, Zap, Clock } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const processSteps = [{
  id: "01",
  title: "Datainnsamling",
  description: "Vår AI kobler seg direkte til din e-post og henter ut alt fra prospekttekster til kundehenvendelser automatisk.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646994/img/xora_data_integration.png",
  icon: Clock
}, {
  id: "02",
  title: "Smart Analyse",
  description: "Xora-motoren analyserer alle e-poster for å identifisere de leadsene med størst potensiale.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646994/img/xora_ai_analysis.png",
  icon: Flame
}, {
  id: "03",
  title: "Automatisert Dialog",
  description: "Systemet sender ut personlig tilpasset oppfølging til de som viser interresse, slik at du kan fokusere på selve visningen og budrunden.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646994/img/xora_automated_dialogue.png",
  icon: ShieldCheck
}, {
  id: "04",
  title: "Avslutning",
  description: "AI-en gir et sammendrag av alt som har blitt skrevet over mail og legger all informasjon inn i kundebildet automatisk, noe som sikrer en effektiv og profesjonell handel.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646994/img/xora_seamless_closing.png",
  icon: Zap
}];
function ProcessStepItem({
  step,
  index
}: {
  step: typeof processSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="223933" data-index={index} ref={ref} className={cn("flex flex-col gap-8 lg:gap-16 items-center relative z-10", isEven ? "lg:flex-row" : "lg:flex-row-reverse")}>
            {}
            <div className="flex-1 w-full flex flex-col sm:flex-row items-start gap-6 lg:gap-8">
                {}
                <div className="relative shrink-0 select-none">
                    <div className="text-7xl sm:text-8xl lg:text-[10rem] font-black font-mono leading-none tracking-tighter">
                        {}
                        <span className="text-transparent" style={{
            WebkitTextStroke: '2px var(--color-border)'
          }}>
                            {step.id}
                        </span>
                        {}
                        <span className="absolute left-0 top-0 text-primary transition-all duration-1000 ease-out" style={{
            clipPath: isVisible ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)'
          }}>
                            {step.id}
                        </span>
                    </div>
                </div>

                {}
                <div className={cn("pt-2 sm:pt-6 lg:pt-10 transition-all duration-700 delay-300", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                            <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default text-foreground">
                            {step.title}
                        </h3>
                    </div>
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-md font-default">
                        {step.description}
                    </p>
                </div>
            </div>

            {}
            <div className="flex-1 w-full">
                <div className={cn("relative aspect-[4/3] sm:aspect-video lg:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl transition-all duration-1000 delay-100 border border-white/10 backdrop-blur-md", isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-12")}>
                    <img src={step.image} alt={step.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl pointer-events-none" />
                </div>
            </div>
        </div>;
}
export default function TheProcess() {
  return <section id="slik-fungerer-xora" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
            {}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none opacity-30" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                
                {}
                <div className="text-center max-w-3xl mx-auto mb-20 lg:mb-32">
                    <h2 className="text-primary font-mono font-semibold tracking-widest uppercase mb-4 text-sm sm:text-base">Arbeidsflyt</h2>
                    <h3 className="text-4xl sm:text-5xl lg:text-6xl font-black font-default mb-6 text-foreground tracking-tight">
                        Fra Prospekt til <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-cyan-400">Salg</span>
                    </h3>
                    <p className="text-muted-foreground text-lg sm:text-xl font-default">Opplev kraften i Xora. Hvert steg er optimalisert for presisjon, hastighet og maksimal verdi for eiendomsmeglere.</p>
                </div>

                {}
                <div className="relative flex flex-col gap-24 lg:gap-40">
                    {}
                    <div className="hidden lg:block absolute left-1/2 top-16 bottom-16 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent -translate-x-1/2 z-0" />

                    {processSteps.map((step, index) => <ProcessStepItem key={step.id} step={step} index={index} />)}
                </div>

                {}
                <div className="mt-24 lg:mt-40 text-center relative z-10">
                    <div className="inline-flex flex-col items-center p-8 sm:p-12 rounded-4xl bg-card/40 border border-white/10 shadow-3xl relative overflow-hidden backdrop-blur-xl">
                        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-50" />
                        <h4 className="text-2xl sm:text-3xl font-bold font-default mb-4 relative z-10">Klar for å automatisere?</h4>
                        <p className="text-muted-foreground mb-8 max-w-md relative z-10 font-default">
                            Vår AI er klar til å transformere din hverdag. Start reisen mot en mer effektiv meglerhverdag i dag.
                        </p>
                        <Button asChild size="lg" className="rounded-xl px-8 py-6 text-lg font-bold relative z-10 group bg-gradient-to-r from-blue-600 to-cyan-500 hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] transition-all duration-300">
                            <Link to="https://xora.no/priser/">
                                Kom i gang nå
                                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Button>
                    </div>
                </div>

            </div>
        </section>;
}